import { CircularProgress, createStyles, Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import useLandingFunctions from "../../../../../hooks/useLandingFunctions";
import { PFXUpsellingComponent } from "../../../../../models/Interfaces";
import { BODY_FONTSIZE_BASE } from "../../../../../pages/Landing";
import { selectIsMobile } from "../../../../../redux/slices/cleverlynkSlice";

import {
  selectComponentDimensions,
  selectGroupCategories,
  selectGroupCategoriesEntities,
  selectGroupItemsEntities,
  selectLandingFromGroup,
} from "../../../../../redux/slices/landingSlice";
import { PFXCategory, selectCategoryEntities, selectItemsEntities } from "../../../../../redux/slices/templateSlice";
import CategoriesProductCard from "../components/CategoriesProductCard";
import CategoriesTabs from "../components/CategoriesTabs";

interface ICategory {
  data: PFXUpsellingComponent;
  loading: boolean;
  id: string;
}

const Category = ({ data, id, loading }: ICategory) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const { width, height } = useSelector(selectComponentDimensions);

  const categoriesEntities = useSelector(selectCategoryEntities);
  const groupCategories = useSelector(selectGroupCategoriesEntities);
  const groupItems = useSelector(selectGroupItemsEntities);
  const items = useSelector(selectItemsEntities);

  const { handleGoToItem } = useLandingFunctions();
  const landingFromGroup = useSelector(selectLandingFromGroup);
  const isMobile = useSelector(selectIsMobile);

  const categories = useMemo(
    () =>
      (data?.categoryIds ?? [])
        .map(c => (landingFromGroup ? groupCategories[c?.elementId ?? ""] : categoriesEntities[c?.elementId ?? ""]))
        .filter(c => !!c),
    [categoriesEntities, data.categoryIds]
  );

  const styles = useMemo(
    () => ({
      fontSize: `${data.title?.fontSizeMultiplier ?? 1 * BODY_FONTSIZE_BASE}rem`,
      color: data.title?.color ?? "#1a1a1a",
      textAlign: (data.title?.alignment ?? "center") as any,
      fontWeight: data.title?.fontWeight as any,
      fontFamily: data.title?.fontFamily,
    }),
    [
      data.title?.alignment,
      data.title?.color,
      data.title?.fontFamily,
      data.title?.fontSizeMultiplier,
      data.title?.fontWeight,
    ]
  );

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      container: {
        maxWidth: width,
        minHeight: height * 0.7,
      },
      root: {
        backgroundColor: data.backgroundColor,
        paddingTop: 20,
        position: "relative",
      },
      scrollablediv: {
        overflowX: "auto",
        marginTop: 10,
        marginRight: 5,
        marginLeft: isMobile ? 5 : 0,
        paddingBottom: 24,
      },
      body: { ...styles },
      bodyTextInput: { ...styles, height: "100%", padding: "10px 8px" },
      bodyDiv: {
        width: "100%",
        borderRadius: 5,
        marginTop: 8,
        padding: "0px 15px",
      },
      bodyInput: {
        width: "100%",
        borderRadius: 5,
      },
    })
  );

  const classes = useStyles();

  useEffect(() => {
    if (categories.length > 0) setSelectedCategory(categories[0]?.id ?? "");
  }, [categories]);

  const itemsToShow = useMemo(() => {
    if (landingFromGroup) {
      return groupCategories[selectedCategory]?.items?.map(i => {
        const item = groupItems[i.id!];
        return !!item ? (
          <CategoriesProductCard
            key={item.id}
            onClick={() => {
              if (categoriesEntities[selectedCategory]?.parent === "rootCategory") {
                handleGoToItem(item, selectedCategory);
              } else {
                handleGoToItem(item, categoriesEntities[selectedCategory]?.parent, selectedCategory);
              }
            }}
            item={item}
            text={data.title!}
            color={data?.title?.color ?? "#1a1a1a"}
          />
        ) : null;
      });
    } else {
      return categoriesEntities[selectedCategory]?.items?.map(i => {
        const item = items[i?.id ?? ""];
        if (item)
          return (
            <CategoriesProductCard
              key={i.id}
              onClick={() => {
                if (categoriesEntities[selectedCategory]?.parent === "rootCategory") {
                  handleGoToItem(item, selectedCategory);
                } else {
                  handleGoToItem(item, categoriesEntities[selectedCategory]?.parent, selectedCategory);
                }
              }}
              item={item}
              text={data.title!}
              color={data?.title?.color ?? "#1a1a1a"}
            />
          );
        else return null;
      });
    }
  }, [categoriesEntities, data.title, groupCategories, groupItems, landingFromGroup, selectedCategory]);

  return (
    <Grid container justifyContent="center" className={classes.root} alignItems="center">
      <div id={`landing-component-${id}`} className={classes.container}>
        <Grid item className={classes.bodyDiv} id={`body-${id}`}>
          <Typography className={classes.body}>{data.title?.content}</Typography>
        </Grid>
        <CategoriesTabs
          categories={categories as PFXCategory[]}
          color={data?.title?.color ?? "#1a1a1a"}
          onCategoryChange={id => setSelectedCategory(id)}
          selectedCategory={selectedCategory}
        />
        <div className={classes.scrollablediv}>
          {loading ? (
            <Grid item container justifyContent="center" alignItems="center" style={{ height: 250 }}>
              <CircularProgress size={50} style={{ color: data?.title?.color }} />
            </Grid>
          ) : (
            <Grid
              container
              wrap="nowrap"
              style={{ marginLeft: isMobile ? 16 : "calc((100% - 83%) / 2)", marginRight: 16 }}>
              {itemsToShow}
            </Grid>
          )}
        </div>
      </div>
    </Grid>
  );
};

export default Category;
